import firebase from "firebase";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import {
  getOptionTitle,
  makeId,
  validateVenuePhoto,
  VenuePhoto
} from "sweet-global";
import BackButton from "sweet-web/components/universal/Button/BackButton";
import NextButton from "sweet-web/components/universal/Button/NextButton";
import OutlineButton from "sweet-web/components/universal/Button/OutlineButton";
import { Label, SectionTitle } from "sweet-web/components/universal/Text";
import {
  OptionWrapper,
  Section,
  Wrapper
} from "sweet-web/components/universal/View";
import { RootState } from "sweet-web/redux";
import * as selectors from "sweet-web/redux/selectors";
import { UpdateVenue } from "sweet-web/redux/venue/actions";
import EditPhoto from "./EditPhoto";

type Props = RouteComponentProps & ConnectedProps<typeof connector>;

const Photos: React.FC<Props> = props => {
  const { venueId } = props.match.params as { venueId: string };
  const { venue, loading } = props;

  const [addingPhoto, setAddingPhoto] = useState(false);

  const back = async () => {
    props.history.push(`/listing/${venueId}/services`);
  };

  const next = async () => {
    props.history.push(`/listing/${venueId}/payment`);
  };

  const savePhoto = async (
    photoId: string,
    photo: VenuePhoto
  ): Promise<boolean> => {
    const photoTag = photo.tag ? photo.tag : null;
    const hasPrimary = _.find(venue.photos, {
      tag: photoTag,
      primary: true
    });
    const photoObj = {
      ...photo,
      primary: hasPrimary ? false : true,
      tag: photoTag
    };
    const validation = validateVenuePhoto(photoObj);
    if (!validation.valid) {
      alert(`Please add ${validation.errors.join(", ")}`);

      return false;
    }
    const id = photoId || makeId();
    await props.UpdateVenue(venueId, { [`photos.${id}`]: photoObj });
    setAddingPhoto(false);

    return true;
  };

  const removePhoto = async (photoId: string) => {
    await props.UpdateVenue(venueId, {
      [`photos.${photoId}`]: firebase.firestore.FieldValue.delete()
    });
  };

  const makePrimary = async (photoId: string, photo: VenuePhoto) => {
    const updateObj: object = {
      [`photos.${photoId}.primary`]: true
    };
    const existingPrimary: string[] = [];
    Object.keys(venue.photos).map(existingPhotoId => {
      const existingPhoto = venue.photos[existingPhotoId];
      if (existingPhoto.primary && existingPhoto.tag == photo.tag) {
        existingPrimary.push(existingPhotoId);
      }
    });
    existingPrimary.forEach(primaryId => {
      updateObj[`photos.${primaryId}.primary`] = false;
    });
    await props.UpdateVenue(venueId, updateObj);
  };

  const spaceTags = Object.keys(venue.spaces).map(spaceId => {
    const space = venue.spaces[spaceId];

    return {
      name: spaceId,
      title: space.name
    };
  });

  const optionTags = Object.keys(venue.options).map(optionId => {
    const option = venue.options[optionId];

    return {
      name: optionId,
      title: getOptionTitle(option)
    };
  });

  const tags = [
    {
      name: "",
      title: venue.name
    },
    ...spaceTags,
    ...optionTags
  ];

  return (
    <Wrapper>
      <Section>
        <SectionTitle style={{ marginBottom: 0 }}>
          Showcase Your Venue
        </SectionTitle>
        <Label>
          Photos help customers get a sense for you space and increase your
          bookings
        </Label>
        <OptionWrapper>
          {Object.keys(venue.photos).map(photoId => {
            const photo = venue.photos[photoId];

            return (
              <EditPhoto
                key={`listing-photo-${photoId}`}
                photoId={photoId}
                photoEntity={photo}
                onSave={photo => {
                  savePhoto(photoId, photo);
                }}
                onRemove={() => removePhoto(photoId)}
                tags={tags}
                makePrimary={makePrimary}
              />
            );
          })}
          {!addingPhoto && (
            <OutlineButton
              style={{ marginTop: 10 }}
              title="Add Photo"
              onClick={() => setAddingPhoto(true)}
            />
          )}
          {addingPhoto && (
            <EditPhoto
              onSave={photo => savePhoto(null, photo)}
              onCancel={() => setAddingPhoto(false)}
              tags={tags}
              makePrimary={makePrimary}
            />
          )}
        </OptionWrapper>
      </Section>
      <BackButton onClick={back} />
      <NextButton primary={true} loading={loading} onClick={next} />
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { venueId } = ownProps.match.params as { venueId: string };

  return {
    venue: selectors.getVenue(venueId)(state),
    loading: selectors.isUpdatingVenue(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      UpdateVenue
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(Photos);
